import React, { useState, useEffect, useCallback } from 'react';
import { User, Phone, Clock, MessageCircle, CheckCircle, Search, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChatLeads = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, new, contacted

  const getAuthHeader = () => {
    const creds = localStorage.getItem('adminAuth');
    return creds ? `Basic ${creds}` : '';
  };

  const fetchLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/chat-leads`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      toast.error('Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const markAsContacted = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/chat-leads/${sessionId}/status?status=contacted`, {
        method: 'PUT',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (res.ok) {
        toast.success('Marked as contacted');
        fetchLeads();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Filter and search leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm);
    
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'new' && (!lead.status || lead.status === 'new')) ||
      (filter === 'contacted' && lead.status === 'contacted');
    
    return matchesSearch && matchesFilter;
  });

  const newLeadsCount = leads.filter(l => !l.status || l.status === 'new').length;
  const contactedCount = leads.filter(l => l.status === 'contacted').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chat Leads</h1>
          <p className="text-gray-500 mt-1">Leads captured from NavMind chat widget</p>
        </div>
        <Button onClick={fetchLeads} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <MessageCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">New Leads</p>
              <p className="text-2xl font-bold text-amber-600">{newLeadsCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Contacted</p>
              <p className="text-2xl font-bold text-green-600">{contactedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or phone..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({leads.length})
            </button>
            <button
              onClick={() => setFilter('new')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'new' ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
              }`}
            >
              New ({newLeadsCount})
            </button>
            <button
              onClick={() => setFilter('contacted')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'contacted' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-600 hover:bg-green-100'
              }`}
            >
              Contacted ({contactedCount})
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-8 text-center">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No leads found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Captured At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredLeads.map((lead, idx) => (
                <tr key={lead.session_id || idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !lead.status || lead.status === 'new' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
                      }`}>
                        <User className="w-5 h-5" />
                      </div>
                      <span className="font-medium text-gray-900">{lead.name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <a 
                      href={`tel:${lead.phone}`}
                      className="flex items-center gap-2 text-blue-600 hover:underline"
                    >
                      <Phone className="w-4 h-4" />
                      {lead.phone || 'N/A'}
                    </a>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full">
                      {lead.source || 'NavMind Chat'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDate(lead.captured_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(!lead.status || lead.status === 'new') ? (
                      <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
                        New
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                        Contacted
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {(!lead.status || lead.status === 'new') && (
                      <Button
                        onClick={() => markAsContacted(lead.session_id)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Contacted
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminChatLeads;
